import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { OpsFeatureFlags } from "@/components/admin/OpsFeatureFlags";
import { StatCard } from "@/components/admin/StatCard";
import { useOpsFlags } from "@/hooks/useOpsFlags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getOpsReport, type OpsReport } from "@/lib/admin/ops.functions";
import { getAdminWalletReport, type AdminWalletReport } from "@/lib/admin/wallet.functions";
import { getProductionReport } from "@/lib/admin/production.functions";
import { MEAL_SLOT_LABEL } from "@/lib/admin/production.schemas";
import { orderSourceLabel } from "@/lib/admin/ops-sources";
import { OPS_STATUS_LABEL, type OpsOrderStatus } from "@/lib/admin/ops.schemas";

export const Route = createFileRoute("/_admin/admin/reports")({
  head: () => ({
    meta: [
      { title: "Operations Reports — Admin" },
      {
        name: "description",
        content: "Order volume, revenue, fulfilment and delivery-area KPIs with CSV export.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ReportsPage,
});

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function rupees(paise: number): string {
  return `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function toCsv(rows: (string | number)[][]): string {
  return rows
    .map((r) =>
      r
        .map((cell) => {
          const s = String(cell ?? "");
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(","),
    )
    .join("\n");
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function ReportsPage() {
  const { flags, isLoading: flagsLoading } = useOpsFlags();
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(30));
  const [dateTo, setDateTo] = useState(isoDaysAgo(-1));

  const fn = useServerFn(getOpsReport);
  const enabled = flags.ops_reports_enabled;

  const report = useQuery<OpsReport>({
    queryKey: ["admin", "reports", dateFrom, dateTo],
    queryFn: () => fn({ data: { dateFrom, dateTo } }),
    enabled: enabled && dateFrom.length === 10 && dateTo.length === 10,
    placeholderData: (prev) => prev,
  });

  const data = report.data;

  const csvRows = useMemo(() => {
    if (!data) return [];
    const rows: (string | number)[][] = [
      ["Report", `${data.range.from} to ${data.range.to}`],
      [],
      ["Metric", "Value"],
      ["Orders", data.totals.orders],
      ["Delivered", data.totals.delivered],
      ["Cancelled / failed", data.totals.cancelled],
      ["In progress", data.totals.inProgress],
      ["Website orders (customer entry)", data.totals.websiteOrders],
      ["Manual orders (admin entry)", data.totals.manualOrders],
      ["Revenue (INR)", (data.totals.revenuePaise / 100).toFixed(2)],
      ["Average order value (INR)", (data.totals.avgOrderValuePaise / 100).toFixed(2)],
      [],
      ["Day", "Orders", "Revenue (INR)"],
      ...data.byDay.map((d) => [d.day, d.orders, (d.revenuePaise / 100).toFixed(2)]),
      [],
      ["Status", "Orders"],
      ...data.byStatus.map((s) => [
        OPS_STATUS_LABEL[s.status as OpsOrderStatus] ?? s.status,
        s.count,
      ]),
      [],
      ["Neighbourhood", "Orders", "Revenue (INR)"],
      ...data.byNeighbourhood.map((n) => [
        n.neighbourhood,
        n.orders,
        (n.revenuePaise / 100).toFixed(2),
      ]),
      [],
      ["Item", "Quantity", "Orders"],
      ...data.topItems.map((t) => [t.title, t.quantity, t.orders]),
    ];
    return rows;
  }, [data]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Reports"
        description="Operations KPIs for the selected delivery-date range."
      />

      {flagsLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !enabled ? (
        <div className="space-y-6">
          <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
            Reports are turned off. Enable{" "}
            <span className="font-mono">OPS_REPORTS_ENABLED</span> below.
          </div>
          <OpsFeatureFlags />
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-[auto_auto_minmax(0,1fr)]">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="From date"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="To date"
            />
            <div className="flex justify-start sm:justify-end">
              <Button
                variant="outline"
                disabled={!data}
                onClick={() =>
                  downloadCsv(
                    `operations-report-${dateFrom}-to-${dateTo}.csv`,
                    toCsv(csvRows),
                  )
                }
              >
                Export CSV
              </Button>
            </div>
          </div>

          {report.error ? (
            <p className="text-sm text-destructive">Couldn't load the report.</p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard label="Orders" value={data?.totals.orders ?? 0} loading={report.isLoading} />
            <StatCard label="Delivered" value={data?.totals.delivered ?? 0} loading={report.isLoading} />
            <StatCard
              label="Cancelled / failed"
              value={data?.totals.cancelled ?? 0}
              loading={report.isLoading}
            />
            <StatCard label="In progress" value={data?.totals.inProgress ?? 0} loading={report.isLoading} />
            <StatCard
              label="Revenue"
              value={rupees(data?.totals.revenuePaise ?? 0)}
              hint="Excludes cancelled and failed orders"
              loading={report.isLoading}
            />
            <StatCard
              label="Website Orders"
              value={data?.totals.websiteOrders ?? 0}
              hint="Entry mode: Customer"
              loading={report.isLoading}
            />
            <StatCard
              label="Manual Orders"
              value={data?.totals.manualOrders ?? 0}
              hint="Entry mode: Admin"
              loading={report.isLoading}
            />
            <StatCard
              label="Average order value"
              value={rupees(data?.totals.avgOrderValuePaise ?? 0)}
              loading={report.isLoading}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <BreakdownCard
              title="Orders by status"
              loading={report.isLoading}
              rows={(data?.byStatus ?? []).map((s) => ({
                label: OPS_STATUS_LABEL[s.status as OpsOrderStatus] ?? s.status,
                value: String(s.count),
              }))}
            />
            <BreakdownCard
              title="Orders by delivery area"
              loading={report.isLoading}
              rows={(data?.byNeighbourhood ?? []).map((n) => ({
                label: n.neighbourhood,
                value: `${n.orders} · ${rupees(n.revenuePaise)}`,
              }))}
            />
            <BreakdownCard
              title="Top items"
              loading={report.isLoading}
              rows={(data?.topItems ?? []).map((t) => ({
                label: t.title,
                value: `${t.quantity} portions`,
              }))}
            />
            <BreakdownCard
              title="Daily volume"
              loading={report.isLoading}
              rows={(data?.byDay ?? []).map((d) => ({
                label: d.day,
                value: `${d.orders} · ${rupees(d.revenuePaise)}`,
              }))}
            />
          </div>

          <ProductionReportSection dateFrom={dateFrom} dateTo={dateTo} />
          
          <WalletReportSection dateFrom={dateFrom} dateTo={dateTo} />

          <OpsFeatureFlags />

        </>
      )}
    </div>
  );
}

function BreakdownCard({
  title,
  rows,
  loading,
}: {
  title: string;
  rows: { label: string; value: string }[];
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-32 w-full" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No data in this range.</p>
        ) : (
          <ul className="max-h-72 space-y-1 overflow-y-auto text-sm">
            {rows.map((r) => (
              <li
                key={r.label}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-border/50 py-1 last:border-0"
              >
                <span className="truncate">{r.label}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {r.value}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Stage D2 — production analytics: source-wise, dish-wise and meal-slot
 * roll-ups for the same delivery-date range, with their own CSV export.
 */
function ProductionReportSection({
  dateFrom,
  dateTo,
}: {
  dateFrom: string;
  dateTo: string;
}) {
  const fn = useServerFn(getProductionReport);
  const q = useQuery({
    queryKey: ["admin", "production-report", dateFrom, dateTo],
    queryFn: () => fn({ data: { from: dateFrom, to: dateTo } }),
    enabled: dateFrom.length === 10 && dateTo.length === 10,
    placeholderData: (prev) => prev,
  });
  const d = q.data;

  const csv = useMemo(() => {
    if (!d) return "";
    const rows: (string | number)[][] = [
      ["Production report", `${d.range.from} to ${d.range.to}`],
      [],
      ["Kitchen summary", ""],
      ["Total orders", d.kitchen.total_orders],
      ["Total portions", d.kitchen.total_items],
      ["Revenue (INR)", (d.kitchen.revenue_paise / 100).toFixed(2)],
      [],
      ["Day", "Orders", "Revenue (INR)"],
      ...d.daily.map((r) => [r.day, r.orders, (r.revenue_paise / 100).toFixed(2)]),
      [],
      ["Order source", "Orders", "Revenue (INR)"],
      ...d.by_source.map((r) => [
        orderSourceLabel(r.source),
        r.orders,
        (r.revenue_paise / 100).toFixed(2),
      ]),
      [],
      ["Dish", "Kind", "Portions"],
      ...d.by_dish.map((r) => [r.title, r.kind, r.quantity]),
      [],
      ["Meal slot", "Portions"],
      ...d.by_slot.map((r) => [MEAL_SLOT_LABEL[r.slot] ?? r.slot, r.quantity]),
    ];
    return toCsv(rows);
  }, [d]);

  return (
    <section aria-label="Production analytics" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-foreground">Production analytics</h2>
        <Button
          variant="outline"
          size="sm"
          disabled={!d}
          onClick={() => downloadCsv(`production-report-${dateFrom}-to-${dateTo}.csv`, csv)}
        >
          Export production CSV
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Total orders" value={d?.kitchen.total_orders ?? 0} loading={q.isLoading} />
        <StatCard label="Total portions" value={d?.kitchen.total_items ?? 0} loading={q.isLoading} />
        <StatCard
          label="Revenue"
          value={rupees(d?.kitchen.revenue_paise ?? 0)}
          loading={q.isLoading}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <BreakdownCard
          title="Source-wise orders"
          loading={q.isLoading}
          rows={(d?.by_source ?? []).map((r) => ({
            label: orderSourceLabel(r.source),
            value: `${r.orders} · ${rupees(r.revenue_paise)}`,
          }))}
        />
        <BreakdownCard
          title="Meal slot summary"
          loading={q.isLoading}
          rows={(d?.by_slot ?? []).map((r) => ({
            label: MEAL_SLOT_LABEL[r.slot] ?? r.slot,
            value: `${r.quantity} portions`,
          }))}
        />
        <BreakdownCard
          title="Dish-wise orders"
          loading={q.isLoading}
          rows={(d?.by_dish ?? []).map((r) => ({
            label: r.title,
            value: `${r.quantity} portions`,
          }))}
        />
        <BreakdownCard
          title="Daily orders"
          loading={q.isLoading}
          rows={(d?.daily ?? []).map((r) => ({
            label: r.day,
            value: `${r.orders} · ${rupees(r.revenue_paise)}`,
          }))}
        />
      </div>
    </section>
  );
}

function WalletReportSection({ dateFrom, dateTo }: { dateFrom: string; dateTo: string }) {
  const fn = useServerFn(getAdminWalletReport);
  const q = useQuery({
    queryKey: ["admin", "wallet-report", dateFrom, dateTo],
    queryFn: () => fn({ data: { from: dateFrom, to: dateTo } }),
    enabled: dateFrom.length === 10 && dateTo.length === 10,
  });
  const d = q.data;

  return (
    <section aria-label="Wallet analytics" className="space-y-4 pt-6 border-t">
      <h2 className="text-sm font-medium text-foreground">Customer Wallet analytics</h2>
      
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Liability" value={rupees(d?.liability_paise ?? 0)} loading={q.isLoading} />
        <StatCard label="Active Wallets" value={d?.wallet_count ?? 0} loading={q.isLoading} />
        <StatCard label="Total Credits" value={rupees(d?.total_credits_paise ?? 0)} loading={q.isLoading} />
        <StatCard label="Total Debits" value={rupees(d?.total_debits_paise ?? 0)} loading={q.isLoading} />
      </div>

      <div className="grid gap-4 lg:grid-cols-1">
        <BreakdownCard
          title="Daily Wallet Activity"
          loading={q.isLoading}
          rows={(d?.daily ?? []).map((r) => ({
            label: r.day,
            value: `+${rupees(r.credit_paise)} / -${rupees(r.debit_paise)}`,
          }))}
        />
      </div>
    </section>
  );
}
